/**
 * LoadoutPicker (#323) — pick exactly `MAX_LOADOUT_SIZE` abilities from the
 * selected champion's full ability pool, shown after `ChampionPicker` on the
 * home/join screens.
 *
 * REASONABLE DEFAULT (per the issue's explicit scope): every champion
 * shipped today has a pool of `MAX_LOADOUT_SIZE` (12) or fewer abilities, so
 * there is nothing real to CHOOSE (subset-wise) yet — the checkbox grid
 * below renders NOTHING in that case (the caller's `value` is already the
 * full pool via `defaultLoadout`), rather than forcing a pointless "select
 * all N of N" click-through. It only renders once a champion's pool
 * actually exceeds 12 abilities.
 *
 * ORDER: subset selection above is a pure SUBSET choice — toggling an
 * ability does not reorder anything; slot order stays the champion's
 * content-canonical order (see `LOADOUT_SCHEMA`'s doc comment in
 * `@pit/protocol` for why the WIRE order is never renumbered).
 *
 * #417: a SEPARATE, always-rendered "slot layout" preview action bar below
 * lets the player drag a selected ability onto any of the 12 bar positions
 * to choose a purely-client-side VISUAL arrangement, persisted locally per
 * champion (`champions/abilityLayout.ts`) — this is what `ActionBar` reads
 * to decide what to render at each bar position, and what `mapper.ts`/
 * `InputSurface` read to resolve a keybind back to the right ability. It is
 * independent of (and renders regardless of) the subset-choice grid above:
 * even a champion with no subset choice to make still has slots worth
 * repositioning (e.g. always keeping a movement ability leftmost).
 *
 * #410: each subset-grid option shows the SAME hover tooltip `ActionBar`
 * shows for its slots (name + authored description + #412 v1 raw-facts
 * block) — reuses the shared `AbilityTooltip` (extracted from
 * `ActionBar.tsx`), anchored BELOW the hovered cell (`placement="below"`)
 * rather than above: unlike `ActionBar`'s single horizontal row, this is a
 * multi-row `auto-fill` grid, so a cell in the top row has no room above it
 * to render into without clipping. Available regardless of selection state
 * or `atCap`-disabled state — players should be able to preview an
 * ability's description/facts even when they can't currently pick it.
 *
 * #799: each FILLED slot-layout cell below shows that same shared tooltip on
 * hover too (it previously had only a native `title` with the bare name —
 * #410 added the tooltip to the subset grid, #417 added the slot-layout bar
 * afterward and never picked it up). Anchored ABOVE (`placement="above"`),
 * unlike the grid: the bar is a single horizontal flex row directly under its
 * own caption, i.e. structurally `ActionBar`'s case, and anchoring below
 * would push the tooltip past the panel's bottom edge.
 */

import { useState, type DragEvent, type ReactElement } from "react";
import { type AbilityId, type ChampionId } from "@pit/protocol";
import { abilityIconLetter, abilityLabel, ACTION_SLOT_COUNT } from "./abilityLabels.js";
import { abilityIconSrc } from "./abilityIcons.js";
import { abilityDescription, abilityFacts } from "./abilityFacts.js";
import { abilityPoolFor } from "./abilityPool.js";
import { AbilityTooltip } from "./AbilityTooltip.js";
import { MAX_LOADOUT_SIZE, needsLoadoutChoice } from "./loadout.js";
import {
  persistSlotOrder,
  resolveCanonicalToVisualSlot,
  resolveSlotOrder,
  resolveVisualSlotToCanonical,
  swapSlotPositions,
} from "./abilityLayout.js";

export interface LoadoutPickerProps {
  readonly championId: ChampionId;
  readonly value: readonly AbilityId[];
  readonly onChange: (ids: readonly AbilityId[]) => void;
  readonly disabled?: boolean;
}

/** Display name for the pool ability at canonical slot `i` — falls back to a
 *  generic "Ability N" for a champion with no `abilityLabels.ts` entry yet
 *  (a real name is #274's auto-derive job, not this component's). */
function poolAbilityLabel(championId: ChampionId, slot: number): string {
  return abilityLabel(championId, slot) ?? `Ability ${String(slot + 1)}`;
}

/** #417: the `dataTransfer` MIME key the slot-layout drag-and-drop uses to
 *  carry the dragged ability's CANONICAL slot number (a plain string of the
 *  integer) — distinct from `text/plain` so it doesn't collide with any
 *  browser-default drag behavior (e.g. dragging selected text). */
const ABILITY_SLOT_DRAG_TYPE = "application/x-pit-ability-slot";

/** A single pool ability option — its own component (not inlined in the
 *  `.map` below) so the #410 hover state can be tracked per-option via a
 *  hook, mirroring `ActionBar.tsx`'s `ActionSlot` pattern. #417: a SELECTED
 *  option is also a drag SOURCE onto the slot-layout bar below — dragging an
 *  unselected option makes no sense (it isn't on the bar to reposition). */
function LoadoutOption({
  championId,
  id,
  slot,
  isSelected,
  atCap,
  disabled,
  onToggle,
}: {
  readonly championId: ChampionId;
  readonly id: AbilityId;
  readonly slot: number;
  readonly isSelected: boolean;
  readonly atCap: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const iconSrc = abilityIconSrc(championId, slot);
  const letter = abilityIconLetter(championId, slot);
  const optionDisabled = disabled || (!isSelected && atCap);
  // #410: local hover tracking only — never touches the store/sim, exactly
  // like `ActionBar.tsx`'s `ActionSlot`. Available regardless of selection
  // state or the disabled/at-cap state — players should be able to preview
  // even what they can't currently pick.
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered ? (
        <AbilityTooltip
          testId={`loadout-option-${id}-tooltip`}
          name={poolAbilityLabel(championId, slot)}
          description={abilityDescription(championId, slot)}
          facts={abilityFacts(championId, slot)}
          placement="below"
        />
      ) : null}
      <label
        data-testid={`loadout-option-${id}`}
        data-selected={isSelected ? "true" : "false"}
        draggable={isSelected && !disabled}
        onDragStart={(e: DragEvent<HTMLLabelElement>) => {
          if (!isSelected || disabled) return;
          e.dataTransfer.setData(ABILITY_SLOT_DRAG_TYPE, String(slot));
          e.dataTransfer.effectAllowed = "move";
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          padding: "4px 8px",
          borderRadius: 4,
          border: `1px solid ${isSelected ? "#5680d6" : "#3a4757"}`,
          background: isSelected ? "#1c2733" : "#161c24",
          opacity: optionDisabled ? 0.5 : 1,
          cursor: isSelected && !disabled ? "grab" : "default",
        }}
      >
        <input type="checkbox" checked={isSelected} disabled={optionDisabled} onChange={onToggle} />
        {iconSrc !== null ? (
          <img src={iconSrc} alt="" width={20} height={20} />
        ) : (
          <span style={{ opacity: 0.6 }}>{letter || "?"}</span>
        )}
        <span>{poolAbilityLabel(championId, slot)}</span>
      </label>
    </div>
  );
}

/**
 * #417: one VISUAL position in the slot-layout preview bar — both a DROP
 * target (drag any selected ability, from a `LoadoutOption` above or from
 * another bar cell, onto it) and, when filled, a drag SOURCE itself (so bar
 * cells can be swapped directly, without going back to the option grid).
 */
function SlotLayoutCell({
  championId,
  visualSlot,
  canonicalSlot,
  abilityId,
  disabled,
  onDropAbility,
}: {
  readonly championId: ChampionId;
  readonly visualSlot: number;
  /** The CANONICAL slot this visual position currently resolves to (via the
   *  shared `resolveVisualSlotToCanonical`), or `null` for an empty position
   *  (fewer selected abilities than bar positions). */
  readonly canonicalSlot: number | null;
  /** The pool ability id currently resolved to this cell, or `null` if the
   *  champion has no ability there or it isn't in the current selection. */
  readonly abilityId: AbilityId | null;
  readonly disabled: boolean;
  /** Called with the CANONICAL slot of whatever was dropped here. */
  readonly onDropAbility: (fromCanonicalSlot: number) => void;
}): ReactElement {
  const [dragOver, setDragOver] = useState(false);
  // #799: local hover tracking only, exactly like `LoadoutOption` above and
  // `ActionBar.tsx`'s `ActionSlot` — never touches the store/sim.
  const [hovered, setHovered] = useState(false);
  const filled = abilityId !== null && canonicalSlot !== null;
  // Safe: `filled` guarantees `canonicalSlot !== null` by construction (the
  // parent only ever supplies a non-null `abilityId` alongside a non-null
  // `canonicalSlot` — see `SlotLayoutBar`).
  const resolvedCanonical = canonicalSlot ?? -1;
  const name = filled ? poolAbilityLabel(championId, resolvedCanonical) : null;
  const iconSrc = filled ? abilityIconSrc(championId, resolvedCanonical) : null;
  const letter = filled ? abilityIconLetter(championId, resolvedCanonical) : "";

  return (
    <div
      data-testid={`loadout-layout-slot-${visualSlot}`}
      data-canonical-slot={canonicalSlot === null ? "" : String(canonicalSlot)}
      data-filled={filled ? "true" : "false"}
      draggable={filled && !disabled}
      onDragStart={(e: DragEvent<HTMLDivElement>) => {
        if (!filled || disabled) return;
        e.dataTransfer.setData(ABILITY_SLOT_DRAG_TYPE, String(resolvedCanonical));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e: DragEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (!e.dataTransfer.types.includes(ABILITY_SLOT_DRAG_TYPE)) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        const raw = e.dataTransfer.getData(ABILITY_SLOT_DRAG_TYPE);
        if (raw === "") return;
        const from = Number.parseInt(raw, 10);
        if (Number.isNaN(from)) return;
        onDropAbility(from);
      }}
      style={{
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 6,
        border: `2px ${dragOver ? "dashed" : "solid"} ${dragOver ? "#7ee787" : filled ? "#5680d6" : "#2c343f"}`,
        background: filled ? "#161c24" : "#10151b",
        color: filled ? "#dfe6ef" : "#3a434f",
        fontSize: 8,
        fontWeight: 600,
        textAlign: "center",
        overflow: "hidden",
        cursor: filled && !disabled ? "grab" : "default",
        position: "relative",
      }}
    >
      {/* #799: rendered as a DIRECT child — this root already carries
          `position: relative` (for the slot-number badge below), so no
          wrapper is needed, and adding one would sit between the cell and
          its flex parent (row layout + drag/drop hit targets).
          `name !== null` is exactly the `filled` case (see above), and gives
          TS the non-null narrowing for the prop. Hidden while `dragOver` is
          true: a tooltip painted over the very cell being dropped onto is
          noise mid-drag (it can't swallow the drop — `AbilityTooltip` sets
          `pointerEvents: none` — but the drop feedback border is what the
          player needs to see there). */}
      {hovered && !dragOver && name !== null ? (
        <AbilityTooltip
          testId={`loadout-layout-slot-${visualSlot}-tooltip`}
          name={name}
          description={abilityDescription(championId, resolvedCanonical)}
          facts={abilityFacts(championId, resolvedCanonical)}
          placement="above"
        />
      ) : null}
      <div style={{ position: "absolute", top: 1, left: 3, fontSize: 8, opacity: 0.6 }}>
        {visualSlot + 1}
      </div>
      {iconSrc !== null ? (
        <img
          src={iconSrc}
          alt=""
          style={{ width: "70%", height: "70%", objectFit: "contain", pointerEvents: "none" }}
        />
      ) : filled ? (
        <span style={{ pointerEvents: "none" }}>{letter || "?"}</span>
      ) : null}
    </div>
  );
}

/**
 * #417: the drag-and-drop slot-layout preview bar — always rendered
 * (independent of whether the subset-choice grid above is), one cell per
 * `ACTION_SLOT_COUNT` visual bar position. Persists to
 * `champions/abilityLayout.ts` on every drop; reloads from storage whenever
 * `championId` changes so switching champions never shows a stale layout.
 */
function SlotLayoutBar({
  championId,
  value,
  disabled,
}: {
  readonly championId: ChampionId;
  readonly value: readonly AbilityId[];
  readonly disabled: boolean;
}): ReactElement {
  const pool = abilityPoolFor(championId);
  // #530: a local render-trigger only — the actual permutation lives in
  // storage (`resolveSlotOrder`/`persistSlotOrder`), read fresh via the
  // shared resolver every render (keyed off the `championId`/`value` props,
  // so a champion switch already re-renders correctly with no extra
  // bookkeeping — see below). Persisting a drop does NOT itself trigger a
  // React re-render (nothing about props/state changed), so `handleDrop`
  // bumps this afterward to force one; storage writes are synchronous, so
  // the resolver already sees the new layout by the time it re-runs.
  const [, bumpRenderKey] = useState(0);

  const handleDrop = (visualSlot: number, fromCanonicalSlot: number): void => {
    const fromVisual = resolveCanonicalToVisualSlot(championId, value, fromCanonicalSlot);
    if (fromVisual === null || fromVisual === visualSlot) return;
    const order = resolveSlotOrder(championId);
    const next = swapSlotPositions(order, visualSlot, fromVisual);
    persistSlotOrder(championId, next);
    bumpRenderKey((k) => k + 1);
  };

  return (
    <div
      data-testid="loadout-layout-bar"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      <div style={{ opacity: 0.75, fontSize: 13 }}>
        Slot layout — drag an ability onto a bar position to rearrange it (saved for this champion)
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {Array.from({ length: ACTION_SLOT_COUNT }, (_, visualSlot) => {
          // #530: THE single resolver — same composition `ActionBar`
          // renders and `InputSurface` casts through — so this preview bar
          // shows exactly what the player will see/cast once submitted.
          const canonicalSlot = resolveVisualSlotToCanonical(championId, value, visualSlot);
          const poolId = canonicalSlot === null ? undefined : pool[canonicalSlot];
          const abilityId = poolId ?? null;
          return (
            <SlotLayoutCell
              key={visualSlot}
              championId={championId}
              visualSlot={visualSlot}
              canonicalSlot={canonicalSlot}
              abilityId={abilityId}
              disabled={disabled}
              onDropAbility={(fromCanonicalSlot) => handleDrop(visualSlot, fromCanonicalSlot)}
            />
          );
        })}
      </div>
    </div>
  );
}

export function LoadoutPicker({
  championId,
  value,
  onChange,
  disabled = false,
}: LoadoutPickerProps): ReactElement {
  const pool = abilityPoolFor(championId);
  const selected = new Set(value);
  const atCap = selected.size >= MAX_LOADOUT_SIZE;
  const showChoiceGrid = needsLoadoutChoice(championId);

  return (
    <div data-testid="loadout-picker" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {showChoiceGrid ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ opacity: 0.75, fontSize: 13 }}>
            Loadout — choose {MAX_LOADOUT_SIZE} of {pool.length} abilities ({selected.size}/
            {MAX_LOADOUT_SIZE} selected)
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 6,
            }}
          >
            {pool.map((id, slot) => {
              const isSelected = selected.has(id);
              return (
                <LoadoutOption
                  key={id}
                  championId={championId}
                  id={id}
                  slot={slot}
                  isSelected={isSelected}
                  atCap={atCap}
                  disabled={disabled}
                  onToggle={() => {
                    if (isSelected) {
                      onChange(value.filter((v) => v !== id));
                    } else if (!atCap) {
                      onChange([...value, id]);
                    }
                  }}
                />
              );
            })}
          </div>
        </div>
      ) : null}
      <SlotLayoutBar championId={championId} value={value} disabled={disabled} />
    </div>
  );
}

/** Is `value` a legal loadout SIZE for `championId` (client-side UX gate —
 *  the server's `resolveLoadout` is the real enforcement)? */
export function isValidLoadoutSize(championId: ChampionId, value: readonly AbilityId[]): boolean {
  const pool = abilityPoolFor(championId);
  const expected = Math.min(MAX_LOADOUT_SIZE, pool.length);
  return value.length === expected;
}
